// src/App.js
import React, { useState, useEffect, useMemo } from "react";
import { Calculator, LogOut, Settings } from "lucide-react";
import {
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  onSnapshot,
} from "firebase/firestore";

import { auth, db } from "./services/firebase";
import useDataSync from "./hooks/useDataSync";
import {
  useCalculator,
  getApronDiscountInfo,
  getDiscountInfo,
} from "./useCalculator";
import {
  PRODUCT_CONFIG,
  SOURCE_OPTIONS,
  LOGO_PROCESSING_OPTIONS,
  SOFA_AREA_TYPES,
} from "./data";

import CalculatorComponent from "./components/Calculator";
import HistoryComponent from "./components/History";
import StyleManagerComponent from "./components/StyleManager";
import SettingsComponent from "./components/Settings";
import Toast from "./components/Toast";

const IS_TEST_MODE = false;
const appId = "urtalequote";

// ── 靜態 Tailwind 主題映射（避免動態拼接導致 production build 遺漏 class）──
const THEME_MAP = {
  dust_mite:    { bg: "bg-blue-600",    text: "text-blue-600",    border: "border-blue-200",    light: "bg-blue-50",    pageBg: "bg-blue-50/30"    },
  sofa_mat:     { bg: "bg-orange-600",  text: "text-orange-600",  border: "border-orange-200",  light: "bg-orange-50",  pageBg: "bg-orange-50/30"  },
  cushion:      { bg: "bg-emerald-600", text: "text-emerald-600", border: "border-emerald-200", light: "bg-emerald-50", pageBg: "bg-emerald-50/30" },
  carpet:       { bg: "bg-purple-600",  text: "text-purple-600",  border: "border-purple-200",  light: "bg-purple-50",  pageBg: "bg-purple-50/30"  },
  edging_carpet:{ bg: "bg-rose-600",    text: "text-rose-600",    border: "border-rose-200",    light: "bg-rose-50",    pageBg: "bg-rose-50/30"    },
  apron:        { bg: "bg-yellow-600",  text: "text-yellow-600",  border: "border-yellow-200",  light: "bg-yellow-50",  pageBg: "bg-yellow-50/30"  },
  wall_sticker: { bg: "bg-cyan-600",    text: "text-cyan-600",    border: "border-cyan-200",    light: "bg-cyan-50",    pageBg: "bg-cyan-50/30"    },
};

// ── 預設 item 工廠函式（統一管理，避免三處重複定義）──
const createDefaultItem = () => ({
  id: Date.now() + Math.random(),
  length: "",
  width: "",
  height: "",
  quantity: 1,
  style: "",
  sofaArea: "free",
  thickness: "5",
  density: "35D",
  materialMult: 1,
  cushionType: "一般坐墊",
  cushionOption: "A",
  isIrregular: false,
  isRounded: false,
  isDoubleSided: false,
  hasStraps: false,
  strapCount: 2,
  hasCuttingFee: false,
  hasPlateFee: false,
  processFee: "",
  wsThickness: "3",
  wsFireType: "normal",
  apronCategory: "",
  logoOption: "none",
  hasModification: false,
  hasStrapChange: false,
  plateCount: 0,
  logoCount: 1,
  selectedLogos: {},
});

const QuotationApp = () => {
  useEffect(() => {
    if (!document.getElementById("tailwind-script")) {
      const s = document.createElement("script");
      s.id = "tailwind-script";
      s.src = "https://cdn.tailwindcss.com";
      document.head.appendChild(s);
    }
  }, []);

  const [user, setUser] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [userRole, setUserRole] = useState("user");
  const [authLoading, setAuthLoading] = useState(true);
  const [allowedUsers, setAllowedUsers] = useState([]);

  const [activeTab, setActiveTab] = useState("calculator");
  const [productType, setProductType] = useState("dust_mite");
  const [editingHistoryId, setEditingHistoryId] = useState(null);
  const [isManualSource, setIsManualSource] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);

  const showToast = (msg) => {
    setToastMsg(msg);
  };

  const getCurrentDateTime = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(now.getDate()).padStart(2, "0")} ${String(
      now.getHours()
    ).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  };

  const [inputs, setInputs] = useState({
    date: getCurrentDateTime(),
    source: "",
    customerName: "",
  });
  const [items, setItems] = useState([createDefaultItem()]);

  const {
    sofaStyles,
    setSofaStyles,
    cushionThicknesses,
    setCushionThicknesses,
    carpetStyles,
    setCarpetStyles,
    edgingCarpetStyles,
    setEdgingCarpetStyles,
    wallStickerStyles,
    setWallStickerStyles,
    apronStyles,
    setApronStyles,
    history,
    setHistory,
  } = useDataSync(db, appId, IS_TEST_MODE);

  const styleDataMap = useMemo(
    () => ({
      sofa_mat: { styles: sofaStyles, set: setSofaStyles, col: "sofa_styles" },
      carpet: {
        styles: carpetStyles,
        set: setCarpetStyles,
        col: "carpet_styles",
      },
      edging_carpet: {
        styles: edgingCarpetStyles,
        set: setEdgingCarpetStyles,
        col: "edging_carpet_styles",
      },
      wall_sticker: {
        styles: wallStickerStyles,
        set: setWallStickerStyles,
        col: "wall_sticker_styles",
      },
      apron: { styles: apronStyles, set: setApronStyles, col: "apron_styles" },
    }),
    [
      sofaStyles,
      carpetStyles,
      edgingCarpetStyles,
      wallStickerStyles,
      apronStyles,
    ]
  );

  const theme = THEME_MAP[productType] || THEME_MAP.dust_mite;

  const calculationResult = useCalculator(
    items,
    productType,
    sofaStyles,
    cushionThicknesses,
    carpetStyles,
    edgingCarpetStyles,
    wallStickerStyles,
    apronStyles
  );

  const relatedRecords = useMemo(() => {
    if (!inputs.customerName) return [];
    return history
      .filter(
        (h) =>
          (h.productType || "dust_mite") === productType &&
          (h.customerName || "").includes(inputs.customerName)
      )
      .slice(0, 5);
  }, [history, inputs.customerName, productType]);

  const filteredHistory = useMemo(
    () => history.filter((r) => (r.productType || "dust_mite") === productType),
    [history, productType]
  );

  useEffect(() => {
    if (IS_TEST_MODE) {
      setUser({ uid: "test", email: "admin@test.com" });
      setIsAuthorized(true);
      setUserRole("admin");
      setAllowedUsers([{ id: "test", email: "admin@test.com", role: "admin" }]);
      setAuthLoading(false);
      return;
    }
    if (!auth) {
      setAuthLoading(false);
      return;
    }
    let unsubscribeSnapshot = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (u) => {
      // 每次 auth 狀態變動時，先清除舊的 snapshot 監聽，避免重複訂閱
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
        unsubscribeSnapshot = null;
      }

      setUser(u);
      if (u) {
        if (!db) {
          setAuthLoading(false);
          return;
        }
        unsubscribeSnapshot = onSnapshot(
          collection(db, "artifacts", appId, "public", "data", "allowed_users"),
          (snap) => {
            const users = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setAllowedUsers(users);
            const r = users.find((ur) => ur.email === u.email);
            if (r) {
              setIsAuthorized(true);
              setUserRole(r.role || "user");
            } else {
              setIsAuthorized(false);
            }
            setAuthLoading(false);
          },
          (error) => {
            console.error("Auth permission error:", error);
            setIsAuthorized(false);
            setAuthLoading(false);
          }
        );
      } else {
        setIsAuthorized(false);
        setAuthLoading(false);
      }
    });

    // 組件卸載時同時清除 auth 與 snapshot 監聽
    return () => {
      unsubscribeAuth();
      if (unsubscribeSnapshot) unsubscribeSnapshot();
    };
  }, []);

  const handleGoogleLogin = async () =>
    !IS_TEST_MODE && auth && signInWithPopup(auth, new GoogleAuthProvider());
  const handleLogout = async () => !IS_TEST_MODE && auth && signOut(auth);

  const clearForm = () => {
    setInputs({ date: getCurrentDateTime(), source: "", customerName: "" });
    setItems([createDefaultItem()]);
    setEditingHistoryId(null);
    setIsManualSource(false);
  };

  // 新增一列時，自動繼承上一列的設定
  const addItemRow = () => {
    setItems((prev) => {
      const lastItem = prev.length > 0 ? prev[prev.length - 1] : null;
      if (lastItem) {
        return [
          ...prev,
          {
            ...lastItem,
            id: Date.now() + Math.random(), // 唯一 ID（避免同毫秒衝突）
            length: "",
            width: "",
            quantity: 1,
            processFee: "",
          },
        ];
      }
      return [...prev, createDefaultItem()];
    });
  };

  const handleItemChange = (id, field, value) =>
    setItems((prev) => {
      // 先套用變更
      const updated = prev.map((item) => {
        if (item.id !== id) return item;
        const newItem = { ...item, [field]: value };
        if (field === "style" && value === "椰棕地毯印刷") {
          newItem.processFee = 1500;
          newItem.hasPlateFee = true;
        }
        return newItem;
      });

      // 除塵墊：用「所有品項的總面積」防止誤取消裁切費
      if (productType === "dust_mite" && field === "hasCuttingFee" && value === false) {
        const totalArea = updated.reduce((sum, it) => {
          return sum + (parseFloat(it.length) || 0) * (parseFloat(it.width) || 0) * (parseInt(it.quantity) || 1);
        }, 0);
        if (totalArea > 0 && totalArea < 5400) {
          showToast("總面積小於5400，強制包含裁切費");
          return updated.map((it) =>
            it.id === id ? { ...it, hasCuttingFee: true } : it
          );
        }
      }

      return updated;
    });

  const validateForm = () =>
    inputs.customerName &&
    inputs.customerName.trim() !== "" &&
    calculationResult.hasValidItems;

  const handleCancelEdit = () => {
    const wasEditing = !!editingHistoryId;
    clearForm();
    showToast("已取消編輯", "info");
    if (wasEditing) {
      setActiveTab("history");
    }
  };

  const loadHistoryRecord = (record, switchTab = true) => {
    setEditingHistoryId(record.id);
    setInputs({
      date: record.date || getCurrentDateTime(),
      source: record.source || "",
      customerName: record.customerName || "",
    });
    if (record.source && !SOURCE_OPTIONS.includes(record.source))
      setIsManualSource(true);
    else setIsManualSource(false);
    if (record.productType) setProductType(record.productType);
    setItems(
      (record.items || []).map((item) => ({
        ...item,
        id: item.id || Math.random(),
        processFee: item.processFee || "",
      }))
    );
    if (switchTab) {
      setActiveTab("calculator");
    }
  };

  const generateItemDescription = (item, idx, customPriceDetails = null) => {
    let desc = `${idx + 1}. `;
    const details = customPriceDetails || item.details;

    if (productType === "apron") {
      const unitPrice = Math.round(details.unitPrice * details.discountRate);
      desc += `【${item.styleName}$${unitPrice}`;

      if (item.selectedLogos && Object.keys(item.selectedLogos).length > 0) {
        let processingTextParts = [];
        Object.entries(item.selectedLogos).forEach(([key, count]) => {
          if (count > 0) {
            const opt = LOGO_PROCESSING_OPTIONS.find((o) => o.value === key);
            if (opt) {
              const name = opt.label.split("(")[0];
              processingTextParts.push(`${name}$${opt.cost}`);
            }
          }
        });
        if (processingTextParts.length > 0) {
          desc += `+加工費(${processingTextParts.join("、")})`;
        }
      }

      if (item.hasModification) desc += `+修改$90`;
      if (item.hasStrapChange) desc += `+改背帶$150`;

      desc += `】X${item.quantity}件`;

      // 圍裙版費
      if (details.plateFee > 0) {
        const count = item.plateCount || 0;
        desc += `+開版費(${count}版)$${details.plateFee}`;
      }

      if (details.machineFee > 0) {
        desc += `+上機費$${details.machineFee}`;
      }

      return desc;
    }

    if (productType === "dust_mite") {
      desc += "除塵墊 ";
      
      const area = (parseFloat(item.length) || 0) * (parseFloat(item.width) || 0);
      const isSmallArea = area > 0 && area < 5400;
      const quantity = parseInt(item.quantity) || 1;

      // 顯示格式：[裁切費$金額]
      if (item.hasCuttingFee || isSmallArea) {
           desc += `[裁切費$${300 * quantity}] `;
      }
      if (item.hasPlateFee) {
           desc += `[版費$300] `;
      }

    } else if (productType === "sofa_mat") {
      desc += item.styleName + " ";
    } else {
      desc += `${item.styleName || item.cushionType} `;
    }

    if (productType === "cushion") {
      const showThickness = !item.cushionType.includes("抱枕");
      if (showThickness) {
        desc += `${
          !item.cushionType.includes("單布套") ? item.density : ""
        } (厚${item.thickness}cm) `;
      }
      let ex = [];
      if (
        item.cushionOption &&
        item.cushionOption !== "A" &&
        item.cushionType.includes("靠墊")
      )
        ex.push(`${item.cushionOption}款`);
      if (item.isIrregular) ex.push("異形");
      if (item.isRounded) ex.push("圓角");
      if (item.isDoubleSided) ex.push("雙面");
      if (item.hasStraps) ex.push(`綁帶x${item.strapCount}`);
      if (ex.length) desc += `[${ex.join("、")}] `;
    }

    if (productType === "wall_sticker")
      desc += `[${item.wsThickness}cm${
        item.wsFireType === "fire" ? "/耐燃" : "/密集板"
      }] `;
    
    if (
      (productType === "edging_carpet" || productType === "carpet") &&
      item.plateFee > 0
    )
      desc += " [排版費]";

    if (productType === "sofa_mat") {
      const areaType = item.sofaArea || "free";
      const areaLabel = SOFA_AREA_TYPES.find((a) => a.value === areaType)?.label;
      const ms = item.sofaMakeSize;
      if (areaLabel) desc += `[${areaLabel}] `;
      if (item.width) {
        desc += `寬${item.width}CM`;
        // 靠背區不使用深度，故不顯示
        if (areaType !== "backrest") desc += ` x 深${item.length}CM`;
        if (areaType === "armrest" && item.height)
          desc += ` x 高${item.height}CM`;
      }
      // 建議製作尺寸另起一行，避免與客人尺寸擠在同一行
      if (ms && areaType !== "free")
        desc += `\n→ 建議製作 寬${ms.width}CM x 深${ms.depth}CM`;
      desc += ` (${item.quantity}件)`;
    } else {
      desc += `${item.width ? item.width + "CMx" + item.length + "CM " : ""}(${
        item.quantity
      }件)`;
    }
    
    if (
      item.warningMsg &&
      productType !== "dust_mite" && 
      productType !== "carpet" &&
      productType !== "edging_carpet"
    )
      desc += ` (註: ${item.warningMsg})`;

    return desc;
  };

  const generateQuotationText = () => {
    let text = `【報價單】\n`;
    calculationResult.items
      .filter((i) => i.isValid)
      .forEach((item, idx) => {
        let desc = generateItemDescription(item, idx);
        const avgPrice = Math.round(item.finalPrice / item.quantity);
        desc += `，金額: $${item.finalPrice} (平均$${avgPrice}/件)`;
        text += desc + "\n";
      });

    // 總金額
    text += `\n總金額: $${calculationResult.grandTotal}${
      calculationResult.isTaxExcluded ? " (未稅)" : ""
    }`;

    // 更新折扣提示文字
    if (calculationResult.nextTierDiff > 0) {
      const groupA = ["sofa_mat", "cushion", "wall_sticker", "apron"];
      if (groupA.includes(productType)) {
        text += `\n\n還差${calculationResult.nextTierDiff.toLocaleString()}元就能享有下一層折扣！可以考慮多做幾件替換，單價會比較低。`;
      } else {
        text += `\n\n還差${calculationResult.nextTierDiff.toLocaleString()}元就能享有下一層折扣！可以考慮做大一點的尺寸或做兩件替換，單價會比較低。`;
      }
    }
    return text;
  };

  const handleCopyAndSave = async () => {
    if (!validateForm()) return alert("資料不完整");
    const t = document.createElement("textarea");
    t.value = generateQuotationText();
    document.body.appendChild(t);
    t.select();
    document.execCommand("copy");
    document.body.removeChild(t);
    
    const validItems = calculationResult.items
      .filter((i) => i.isValid)
      .map((item) => {
        const s = { ...item };
        delete s.debugInfo;
        return s;
      });

    const baseData = {
      productType,
      customerName: inputs.customerName,
      source: inputs.source,
      date: inputs.date,
      items: validItems,
      totalAmount: calculationResult.grandTotal,
      createdBy: { uid: user?.uid, email: user?.email },
    };

    if (!editingHistoryId) {
      baseData.timestamp = new Date().toISOString();
    }

    const wasEditing = !!editingHistoryId;

    if (!IS_TEST_MODE && db) {
      try {
        if (editingHistoryId) {
          await updateDoc(
            doc(db, "artifacts", appId, "public", "data", "quotes", editingHistoryId),
            baseData
          );
        } else {
          await addDoc(
            collection(db, "artifacts", appId, "public", "data", "quotes"),
            baseData
          );
        }
      } catch (e) {
        console.error("Save error:", e);
        showToast("❌ 儲存失敗，請重試");
        return;
      }
    }
    showToast("已複製並儲存成功！");
    clearForm();

    if (wasEditing) {
      setActiveTab("history");
    }
  };

  const handleCopyItemizedPrices = () => {
    let text = `【${PRODUCT_CONFIG[productType]?.label} 單品價格列表】\n`;

    calculationResult.items
      .filter((i) => i.isValid)
      .forEach((item, idx) => {
        let finalPrice = item.finalPrice;
        let details = item.details;

        if (productType === "apron") {
          const { discountRate } = getApronDiscountInfo(
            item.quantity,
            details.unitPrice
          );
          
          const discountedProductPrice = Math.round(details.unitPrice * discountRate);

          // 單品價格：以「單獨下單」重算，故上機費收整筆 1000
          // POLO工作服：未滿 10 件即收（不論有無加工）；其他分類：有加工且未滿 7 件才收
          let machineFee = 0;
          if (details.apronCategory === "POLO工作服") {
            if (item.quantity < 10) machineFee = 1000;
          } else if (details.hasProcessing && item.quantity < 7) {
            machineFee = 1000;
          }

          finalPrice = Math.round(
            (discountedProductPrice + details.processingFeePerItem) *
              item.quantity +
              details.plateFee +
              machineFee
          );

          details = {
            ...details,
            discountRate,
            machineFee,
          };
        } else {
          const { discountRate } = getDiscountInfo(item.twdBaseSellingPrice);
          let cuttingFee = 0;
          if (productType === "dust_mite") {
             // 這裡無法取得 totalArea，所以只能用單品判斷
             // 為了避免複製價格與總價不符，建議直接使用 item.cuttingFee (由 useCalculator 算好的)
             // 但 item.cuttingFee 是總額，單品價格通常需要單價...
             // 簡化：直接使用 useCalculator 算好的 cuttingFee
             cuttingFee = item.cuttingFee || 0;
          } else {
             cuttingFee = item.cuttingFee || 0;
          }

          finalPrice =
            Math.round(item.twdBaseSellingPrice * discountRate) +
            (item.strapCost || 0) +
            (item.transportFee || 0) +
            cuttingFee +
            (item.plateFee || 0);
        }

        let desc = generateItemDescription(item, idx, details);
        const avgPrice = Math.round(finalPrice / item.quantity);
        desc += `，金額: $${finalPrice} (平均$${avgPrice}/件)`;
        
        text += desc + "\n";
      });

    const t = document.createElement("textarea");
    t.value = text;
    document.body.appendChild(t);
    t.select();
    document.execCommand("copy");
    document.body.removeChild(t);
    showToast("已複製單品價格！");
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center">載入中...</div>;
  if (!user && !IS_TEST_MODE) return <div className="min-h-screen flex items-center justify-center bg-slate-100"><button onClick={handleGoogleLogin} className="bg-blue-600 text-white px-6 py-3 rounded-xl">Google 登入</button></div>;
  if (!isAuthorized && !IS_TEST_MODE) return <div className="min-h-screen flex items-center justify-center">權限不足，請聯繫管理員開通權限。</div>;

  return (
    <div className={`min-h-screen font-sans text-slate-800 p-4 md:p-8 transition-colors duration-500 ${theme.pageBg}`}>
      {toastMsg && <Toast message={toastMsg} onClose={() => setToastMsg(null)} />}
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col gap-4">
          <div className="flex gap-2 bg-white p-1.5 rounded-2xl shadow-sm w-full md:w-fit mx-auto overflow-x-auto">
            {Object.entries(PRODUCT_CONFIG).map(([k, v]) => (
              <button
                key={k}
                disabled={!!editingHistoryId}
                onClick={() => {
                  setProductType(k);
                  if (activeTab === "styles" && k === "dust_mite") setActiveTab("calculator");
                }}
                className={`flex-1 md:flex-none px-4 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 whitespace-nowrap ${
                  productType === k ? theme.bg + " text-white shadow-md" : "text-slate-500 hover:bg-slate-100"
                } ${!!editingHistoryId ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <v.icon size={18} /> {v.label}
              </button>
            ))}
          </div>
          <header className={`${theme.bg} text-white p-6 rounded-2xl shadow-lg flex flex-col md:flex-row items-center justify-between gap-4 transition-colors duration-300`}>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Calculator size={28} /> {PRODUCT_CONFIG[productType]?.label} 報價系統 <span className="text-xs bg-white/20 px-2 py-1 rounded border border-white/30">{IS_TEST_MODE ? "測試" : "Team"}</span>
              </h1>
            </div>
            <div className="flex items-center gap-4 bg-black/20 p-2 pl-4 pr-2 rounded-full">
              <div className="flex flex-col text-right mr-4 text-white/90 text-sm">
                <span>{user.displayName || "使用者"}</span>
                <span className="text-xs opacity-70">{userRole === "admin" ? "管理員" : "一般人員"}</span>
              </div>
              {!IS_TEST_MODE && <button onClick={handleLogout} className="p-2 bg-white/10 hover:bg-white/20 rounded-full" title="登出"><LogOut size={16} /></button>}
              {userRole === "admin" && <button onClick={() => setActiveTab("settings")} className={`ml-2 p-2 rounded-full transition-all ${activeTab === "settings" ? "bg-white text-slate-800" : "bg-black/20 text-white hover:bg-black/30"}`} title="設定"><Settings size={18} /></button>}
            </div>
          </header>
        </div>

        <div className="flex gap-2 bg-white p-1 rounded-xl shadow-sm w-fit">
          <button onClick={() => setActiveTab("calculator")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "calculator" ? theme.light + " " + theme.text : "text-slate-500 hover:bg-slate-50"}`}>{editingHistoryId ? "編輯紀錄中" : "計算報價"}</button>
          <button onClick={() => setActiveTab("history")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === "history" ? theme.light + " " + theme.text : "text-slate-500 hover:bg-slate-50"}`}>歷史紀錄 <span className="bg-slate-200 text-slate-600 px-1.5 rounded-full text-xs">{filteredHistory.length}</span></button>
          <button onClick={() => setActiveTab("styles")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === "styles" ? theme.light + " " + theme.text : "text-slate-500 hover:bg-slate-50"}`}>款式管理</button>
        </div>

        {activeTab === "calculator" && (
          <CalculatorComponent
            theme={theme}
            inputs={inputs}
            setInputs={setInputs}
            items={items}
            setItems={setItems}
            productType={productType}
            editingHistoryId={editingHistoryId}
            isManualSource={isManualSource}
            setIsManualSource={setIsManualSource}
            handleItemChange={handleItemChange}
            addItemRow={addItemRow}
            clearForm={clearForm}
            handleCancelEdit={handleCancelEdit}
            handleCopyAndSave={handleCopyAndSave}
            validateForm={validateForm}
            handleCopyItemizedPrices={handleCopyItemizedPrices}
            generateQuotationText={generateQuotationText}
            calculationResult={calculationResult}
            relatedRecords={relatedRecords}
            loadHistoryRecord={loadHistoryRecord}
            sofaStyles={sofaStyles}
            carpetStyles={carpetStyles}
            edgingCarpetStyles={edgingCarpetStyles}
            wallStickerStyles={wallStickerStyles}
            cushionThicknesses={cushionThicknesses}
            apronStyles={apronStyles}
            userRole={userRole}
            showToast={showToast}
          />
        )}
        {activeTab === "history" && <HistoryComponent history={filteredHistory} loadHistoryRecord={loadHistoryRecord} db={db} appId={appId} IS_TEST_MODE={IS_TEST_MODE} editingHistoryId={editingHistoryId} />}
        {activeTab === "styles" && <StyleManagerComponent productType={productType} styleDataMap={styleDataMap} cushionThicknesses={cushionThicknesses} setCushionThicknesses={setCushionThicknesses} db={db} appId={appId} IS_TEST_MODE={IS_TEST_MODE} showToast={showToast} />}
        {activeTab === "settings" && <SettingsComponent allowedUsers={allowedUsers} user={user} db={db} appId={appId} IS_TEST_MODE={IS_TEST_MODE} />}
      </div>
    </div>
  );
};

export default QuotationApp;